import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  listServices,
  fetchAdminProfile,
  requestServiceCorrection,
  editServiceBySuperadmin,
  deleteService,
  listInventory,
} from "../../api/api";
import { SearchContext } from "../../context/SearchContext";
import styles from "./StringerDashboard.module.css";

const StringerDashboard = () => {
  const navigate = useNavigate();
  const { searchTerm: navbarSearchTerm } = useContext(SearchContext);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userLocation, setUserLocation] = useState("");
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    serviceType: "all",
    location: "all",
    searchTerm: "",
    showOnlyEditing: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [correctionReason, setCorrectionReason] = useState("");
  const [editingService, setEditingService] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);

  useEffect(() => {
    const role = localStorage.getItem("adminRole");
    setUserRole(role || "");
    if (role === "stringer") {
      fetchUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userRole === "stringer" && userLocation) {
      fetchServices();
    } else if (userRole === "superadmin") {
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.startDate,
    filters.endDate,
    filters.serviceType,
    filters.location,
    userLocation,
    userRole,
  ]);

  // Location is already in display format: "Jumeirah Store", "Marina Store", "Dubai Hills Store"
  // No normalization needed - send exact names to backend

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) return;

      const response = await fetchAdminProfile(token);
      const profile = response.data;
      setUserLocation(profile.location || "");
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const params = { 
        limit: 1000,
        start_date: filters.startDate,
        end_date: filters.endDate
      };

      // For stringers, we need to fetch services where:
      // 1. Location matches their store, OR
      // 2. Racket is from Marina but serviced at their location
      // We'll fetch all services and filter client-side for stringers
      if (userRole === "stringer" && userLocation) {
        console.log("Stringer location:", userLocation);
        // Don't send location param for stringers - we'll filter client-side
        // to include cross-location services
      }
      // For superadmin, filter by selected location if not "all"
      else if (userRole === "superadmin" && filters.location !== "all") {
        params.location = filters.location;
      }

      console.log("Fetching services with params:", params);
      const response = await listServices(params);
      console.log("Services received:", response.data);

const matchLocation = (locA, locB) => {
  if (!locA || !locB) return false;
  const a = String(locA).toLowerCase().replace(/\s*store\s*$/i, "").trim();
  const b = String(locB).toLowerCase().replace(/\s*store\s*$/i, "").trim();
  if (a === b) return true;
  if ((a.includes("jume") || a.includes("jumer")) && (b.includes("jume") || b.includes("jumer"))) return true;
  if (a.includes("marin") && b.includes("marin")) return true;
  if (a.includes("dubai") && b.includes("dubai")) return true;
  return false;
};

      // For stringers, filter to show services at their location
      // OR services where racket is from Marina but serviced at their location
      if (userRole === "stringer" && userLocation) {
        const stringerServices = response.data.filter((service) => {
          // Show if service location matches stringer's location
          const isAtStringerLocation = matchLocation(service.location, userLocation);

          // Show if racket is from Marina but serviced at stringer's location
          const isFromMarinaServicedHere =
            service.racket_from_marina === "yes" &&
            matchLocation(service.serviced_at_location, userLocation);

          return isAtStringerLocation || isFromMarinaServicedHere;
        });
        setServices(stringerServices);
      } else {
        setServices(response.data);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      alert("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = services.filter((service) => {
    // Date filtering is now done by the backend
    const matchesServiceType =
      filters.serviceType === "all" ||
      service.service_type === filters.serviceType;

    // Check both local search and navbar search
    const searchTermToUse = navbarSearchTerm || filters.searchTerm;
    const matchesSearch =
      !searchTermToUse ||
      service.customer_name
        ?.toLowerCase()
        .includes(searchTermToUse.toLowerCase()) ||
      service.string_name
        ?.toLowerCase()
        .includes(searchTermToUse.toLowerCase()) ||
      service.brand?.toLowerCase().includes(searchTermToUse.toLowerCase()) ||
      service.receipt_no
        ?.toLowerCase()
        .includes(searchTermToUse.toLowerCase()) ||
      service.performed_by
        ?.toLowerCase()
        .includes(searchTermToUse.toLowerCase());

    // Filter by correction request status for superadmin
    const matchesEditingFilter =
      !filters.showOnlyEditing ||
      (service.correction_requested === "yes" && service.correction_resolved === "no");

    return matchesServiceType && matchesSearch && matchesEditingFilter;
  });

  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedServices = filteredServices.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const stats = {
    totalServices: filteredServices.length,
    totalRevenue: filteredServices.reduce(
      (sum, s) =>
        sum + (parseFloat(s.total_payment) || parseFloat(s.cost_per_unit) || 0),
      0
    ),
    onlineServices: filteredServices.filter((s) => s.service_type === "online")
      .length,
    offlineServices: filteredServices.filter(
      (s) => s.service_type === "offline"
    ).length,
    walkInServices: filteredServices.filter((s) => s.service_type === "walk-in")
      .length,
  };

  const handleRequestCorrection = (service) => {
    setSelectedService(service);
    setShowCorrectionModal(true);
  };

  const submitCorrectionRequest = async () => {
    if (!correctionReason.trim()) {
      alert("Please provide a reason for the correction request");
      return;
    }

    try {
      await requestServiceCorrection(
        selectedService.service_id,
        correctionReason
      );
      setSuccessMessage("Correction request submitted successfully!");
      setShowCorrectionModal(false);
      setCorrectionReason("");
      setSelectedService(null);
      fetchServices();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error submitting correction request:", error);
      alert(
        error.response?.data?.message || "Failed to submit correction request"
      );
    }
  };

  const fetchInventory = async () => {
    try {
      setLoadingInventory(true);
      const response = await listInventory();
      const filteredData = response.data.filter(
        (item) => item.string_name !== "Bring Own String" && item.remaining_quantity > 0
      );
      setInventory(filteredData);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleEditService = (service) => {
    setEditingService(service.service_id);
    setEditedData({
      customer_name: service.customer_name || "",
      customer_phone: service.customer_phone || "",
      customer_email: service.customer_email || "",
      racket_name: service.racket_name || "",
      tension_info: service.tension_info || "",
      total_payment: service.total_payment || "",
      receipt_no: service.receipt_no || "",
      remarks: service.remarks || "",
      racket_from_marina: service.racket_from_marina || "no",
      serviced_at_location: service.serviced_at_location || "",
      string_id: service.string_id || "",
      string_name: service.string_name || "",
      custom_string_name: service.custom_string_name || "",
      customer_string: service.customer_string || "no",
    });
    // Fetch inventory for dropdown
    if (userRole === "superadmin" && service.customer_string !== "yes") {
      fetchInventory();
    }
  };

  const handleSaveEdit = async (serviceId) => {
    try {
      await editServiceBySuperadmin(serviceId, editedData);
      setSuccessMessage("Service updated successfully!");
      setEditingService(null);
      setEditedData({});
      fetchServices();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error updating service:", error);
      alert(error.response?.data?.message || "Failed to update service");
    }
  };

  const handleCancelEdit = () => {
    setEditingService(null);
    setEditedData({});
  };

  const handleEditChange = (field, value) => {
    setEditedData({ ...editedData, [field]: value });
  };

  const handleDeleteService = (service) => {
    setServiceToDelete(service);
    setShowDeleteModal(true);
  };

  const confirmDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      await deleteService(serviceToDelete.service_id);
      setSuccessMessage("Service deleted successfully!");
      setShowDeleteModal(false);
      setServiceToDelete(null);
      fetchServices();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error deleting service:", error);
      alert(error.response?.data?.message || "Failed to delete service");
      setShowDeleteModal(false);
      setServiceToDelete(null);
    }
  };

  const cancelDeleteService = () => {
    setShowDeleteModal(false);
    setServiceToDelete(null);
  };

  const servicesByStringer = useMemo(() => {
    const stringerMap = {};
    filteredServices.forEach((service) => {
      const stringer = service.performed_by || "Unknown";
      if (!stringerMap[stringer]) {
        stringerMap[stringer] = { stringer, count: 0 };
      }
      stringerMap[stringer].count += 1;
    });
    return Object.values(stringerMap)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [filteredServices]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingText}>Loading services...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.headerLeft}>
          <button
            onClick={() => navigate("/dashboard")}
            className={styles.backButton}
            title="Back to Dashboard"
          >
            ← Back
          </button>
          <h1 className={styles.header}>
            Recorded Services
            {userRole === "stringer" && userLocation
              ? ` - ${userLocation}`
              : ""}
          </h1>
        </div>
        {userRole === "superadmin" && (
          <button
            onClick={() => navigate("/analytics-dashboard")}
            className={styles.analyticsButton}
          >
            View Analytics
          </button>
        )}
      </div>

      {successMessage && (
        <div className={styles.successMessage}>{successMessage}</div>
      )}

      {(userRole === "superadmin" || userRole === "stringer") && (
        <div className={styles.filtersCard}>
          <div className={styles.filtersGrid}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => {
                  const newStartDate = e.target.value;
                  const newFilters = { ...filters, startDate: newStartDate };
                  // If end date is before new start date, update end date to match start date
                  if (filters.endDate < newStartDate) {
                    newFilters.endDate = newStartDate;
                  }
                  setFilters(newFilters);
                }}
                className={styles.filterInput}
              />
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>End Date</label>
              <input
                type="date"
                value={filters.endDate}
                min={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
                className={styles.filterInput}
              />
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Service Type</label>
              <select
                value={filters.serviceType}
                onChange={(e) =>
                  setFilters({ ...filters, serviceType: e.target.value })
                }
                className={styles.filterInput}
              >
                <option value="all">All Types</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="walk-in">Walk-in</option>
              </select>
            </div>

            {userRole === "superadmin" && (
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Location</label>
                <select
                  value={filters.location}
                  onChange={(e) =>
                    setFilters({ ...filters, location: e.target.value })
                  }
                  className={styles.filterInput}
                >
                  <option value="all">All Locations</option>
                  <option value="Jumeirah Store">Jumeirah Store</option>
                  <option value="Marina Store">Marina Store</option>
                  <option value="Dubai Hills Store">Dubai Hills Store</option>
                </select>
              </div>
            )}

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Search</label>
              <input
                type="text"
                placeholder="Customer, String, Receipt, Stringer..."
                value={filters.searchTerm}
                onChange={(e) =>
                  setFilters({ ...filters, searchTerm: e.target.value })
                }
                className={styles.filterInput}
              />
            </div>

            {userRole === "superadmin" && (
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Edit Requests</label>
                <button
                  onClick={() =>
                    setFilters({
                      ...filters,
                      showOnlyEditing: !filters.showOnlyEditing,
                    })
                  }
                  className={`${styles.editRequestsButton} ${
                    filters.showOnlyEditing ? styles.editRequestsButtonActive : ""
                  }`}
                >
                  <span className={styles.buttonIcon}>
                    {filters.showOnlyEditing ? "✓" : "⚑"}
                  </span>
                  <span className={styles.buttonText}>
                    {filters.showOnlyEditing ? "Show All" : "Edit Request"}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {userRole === "superadmin" && (
        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricHeader}>
              <div
                className={styles.metricIcon}
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              <div className={styles.metricLabel}>Total Services</div>
            </div>
            <div className={styles.metricValue}>{stats.totalServices}</div>
            <div className={styles.metricFooter}>
              <span className={styles.metricChange}>
                {filters.location !== "all"
                  ? filters.location
                  : "All Locations"}
              </span>
            </div>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricHeader}>
              <div
                className={styles.metricIcon}
                style={{
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </div>
              <div className={styles.metricLabel}>Total Revenue</div>
            </div>
            <div className={styles.metricValue}>
              AED {stats.totalRevenue.toFixed(2)}
            </div>
            <div className={styles.metricFooter}>
              <span className={styles.metricChange}>
                Avg: AED{" "}
                {filteredServices.length > 0
                  ? (stats.totalRevenue / filteredServices.length).toFixed(2)
                  : "0.00"}
              </span>
            </div>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricHeader}>
              <div
                className={styles.metricIcon}
                style={{
                  background:
                    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="8.5" cy="7" r="4"></circle>
                  <line x1="20" y1="8" x2="20" y2="14"></line>
                  <line x1="23" y1="11" x2="17" y2="11"></line>
                </svg>
              </div>
              <div className={styles.metricLabel}>Active Stringers</div>
            </div>
            <div className={styles.metricValue}>
              {servicesByStringer.length}
            </div>
            <div className={styles.metricFooter}>
              <span className={styles.metricChange}>
                Contributing to services
              </span>
            </div>
          </div>
        </div>
      )}

      <div className={styles.servicesHeader}>
        <h2 className={styles.servicesTitle}>
          {userRole === "superadmin" ? "Service Records" : "My Services"}
        </h2>
        <div className={styles.serviceCount}>
          Showing {paginatedServices.length} of {filteredServices.length}{" "}
          services
        </div>
      </div>

      <div className={styles.cardsGrid}>
        {paginatedServices.length > 0 ? (
          <>
            {paginatedServices.map((service) => {
              const isEditing = editingService === service.service_id;
              return (
                <div
                  key={service.service_id}
                  className={`${styles.serviceCard} ${
                    service.correction_requested === "yes" &&
                    service.correction_resolved === "no"
                      ? styles.correctionRequested
                      : ""
                  }`}
                >
                  <div className={styles.cardHeader}>
                    <div className={styles.cardDate}>
                      {formatDate(service.service_date)}
                    </div>
                    <div className={styles.cardAmount}>
                      {isEditing ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span className="dirham-symbol">&#xea;</span>
                          <input
                            type="number"
                            step="0.01"
                            value={editedData.total_payment}
                            onChange={(e) =>
                              handleEditChange("total_payment", e.target.value)
                            }
                            className={styles.editInput}
                            style={{ width: "80px" }}
                          />
                        </div>
                      ) : (
                        <>
                          <span className="dirham-symbol">&#xea;</span>{" "}
                          {parseFloat(
                            service.total_payment || service.cost_per_unit || 0
                          ).toFixed(2)}
                        </>
                      )}
                    </div>
                  </div>

                  <div className={styles.cardRow}>
                    <div className={styles.cardLabel}>String</div>
                    <div
                      className={`${styles.cardValue} ${
                        service.customer_string === "yes"
                          ? styles.highlight
                          : ""
                      }`}
                    >
                      {isEditing && service.customer_string === "yes" ? (
                        <input
                          type="text"
                          value={editedData.custom_string_name}
                          onChange={(e) =>
                            handleEditChange(
                              "custom_string_name",
                              e.target.value
                            )
                          }
                          className={styles.editInput}
                          placeholder="Customer string name"
                        />
                      ) : isEditing && service.customer_string !== "yes" ? (
                        <select
                          value={editedData.string_id || service.string_id || ""}
                          onChange={(e) => {
                            const selectedString = inventory.find(
                              (s) => s.string_id === parseInt(e.target.value)
                            );
                            if (selectedString) {
                              setEditedData({
                                ...editedData,
                                string_id: e.target.value,
                                string_name: selectedString.string_name,
                              });
                            }
                          }}
                          className={styles.editInput}
                          disabled={loadingInventory}
                        >
                          {loadingInventory ? (
                            <option value="">Loading strings...</option>
                          ) : (
                            <>
                              <option value="">Select a string</option>
                              {inventory.map((string) => (
                                <option key={string.string_id} value={string.string_id}>
                                  {string.string_name}
                                  {string.brand ? ` (${string.brand})` : ""} - Stock: {string.remaining_quantity}
                                </option>
                              ))}
                            </>
                          )}
                        </select>
                      ) : service.customer_string === "yes" ? (
                        service.custom_string_name || "Customer String"
                      ) : (
                        service.string_name || "-"
                      )}
                    </div>
                  </div>

                  <div className={styles.cardRow}>
                    <div className={styles.cardLabel}>Customer</div>
                    <div className={styles.cardValue}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedData.customer_name}
                          onChange={(e) =>
                            handleEditChange("customer_name", e.target.value)
                          }
                          className={styles.editInput}
                        />
                      ) : (
                        service.customer_name || "-"
                      )}
                    </div>
                  </div>

                  {(service.customer_phone ||
                    service.customer_email ||
                    isEditing) && (
                    <div className={styles.cardRow}>
                      <div className={styles.cardLabel}>Contact</div>
                      <div className={styles.cardValue}>
                        {isEditing ? (
                          <>
                            <input
                              type="text"
                              value={editedData.customer_phone}
                              onChange={(e) =>
                                handleEditChange(
                                  "customer_phone",
                                  e.target.value
                                )
                              }
                              className={styles.editInput}
                              placeholder="Phone"
                            />
                            <input
                              type="email"
                              value={editedData.customer_email}
                              onChange={(e) =>
                                handleEditChange(
                                  "customer_email",
                                  e.target.value
                                )
                              }
                              className={styles.editInput}
                              placeholder="Email"
                              style={{ marginTop: "4px" }}
                            />
                          </>
                        ) : (
                          <>
                            {service.customer_phone}
                            {service.customer_email &&
                              service.customer_phone && <br />}
                            {service.customer_email && (
                              <span
                                style={{ fontSize: "0.9em", color: "#6c757d" }}
                              >
                                {service.customer_email}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className={styles.cardRow}>
                    <div className={styles.cardLabel}>Stringer</div>
                    <div className={styles.cardValue}>
                      {service.performed_by || "Unknown"}
                    </div>
                  </div>

                  {(service.racket_name ||
                    service.racket_info ||
                    isEditing) && (
                    <div className={styles.cardRow}>
                      <div className={styles.cardLabel}>Racket</div>
                      <div className={styles.cardValue}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedData.racket_name}
                            onChange={(e) =>
                              handleEditChange("racket_name", e.target.value)
                            }
                            className={styles.editInput}
                          />
                        ) : (
                          <>
                            {service.racket_name}
                            {service.racket_info && service.racket_name && (
                              <br />
                            )}
                            {service.racket_info && (
                              <span
                                style={{ fontSize: "0.9em", color: "#6c757d" }}
                              >
                                {service.racket_info}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {(service.tension_info || isEditing) && (
                    <div className={styles.cardRow}>
                      <div className={styles.cardLabel}>Tension</div>
                      <div className={styles.cardValue}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedData.tension_info}
                            onChange={(e) =>
                              handleEditChange("tension_info", e.target.value)
                            }
                            className={styles.editInput}
                          />
                        ) : (
                          service.tension_info
                        )}
                      </div>
                    </div>
                  )}

                  {(service.receipt_no || isEditing) && (
                    <div className={styles.cardRow}>
                      <div className={styles.cardLabel}>Receipt #</div>
                      <div className={styles.cardValue}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedData.receipt_no}
                            onChange={(e) =>
                              handleEditChange("receipt_no", e.target.value)
                            }
                            className={styles.editInput}
                          />
                        ) : (
                          service.receipt_no
                        )}
                      </div>
                    </div>
                  )}

                  <div className={styles.cardRow}>
                    <div className={styles.cardLabel}>Racket From</div>
                    <div className={styles.cardValue}>
                      {service.racket_from_marina === "yes" ? (
                        <span className={styles.marinaOrigin}>
                          Marina Store
                        </span>
                      ) : (
                        <span className={styles.localService}>
                          {service.location || "-"}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.cardRow}>
                    <div className={styles.cardLabel}>Serviced At</div>
                    <div className={styles.cardValue}>
                      <span className={styles.servicedLocation}>
                        {service.racket_from_marina === "yes"
                          ? service.serviced_at_location ||
                            service.location ||
                            "-"
                          : service.location || "-"}
                      </span>
                    </div>
                  </div>

                  {service.correction_requested === "yes" &&
                    service.correction_reason && (
                      <div className={styles.cardRow}>
                        <div className={styles.cardLabel}>
                          {service.correction_resolved === "yes"
                            ? "Edit Request (Resolved)"
                            : "Edit Request"}
                        </div>
                        <div
                          className={`${styles.cardValue} ${styles.correctionReasonText}`}
                        >
                          {service.correction_reason}
                        </div>
                      </div>
                    )}

                  {(userRole === "stringer" || userRole === "superadmin") && (
                    <div className={styles.cardRow}>
                      <div className={styles.cardLabel}>Actions</div>
                      <div className={styles.cardValue}>
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => handleSaveEdit(service.service_id)}
                              className={`${styles.actionBtn} ${styles.saveBtn}`}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className={`${styles.actionBtn} ${styles.cancelBtn}`}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            {userRole === "superadmin" && (
                              <>
                                <button
                                  onClick={() => handleEditService(service)}
                                  className={`${styles.actionBtn} ${styles.editBtn}`}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteService(service)}
                                  className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                            {userRole === "stringer" &&
                              service.correction_requested !== "yes" && (
                                <button
                                  onClick={() =>
                                    handleRequestCorrection(service)
                                  }
                                  className={`${styles.actionBtn} ${styles.correctionBtn}`}
                                >
                                  Request Correction
                                </button>
                              )}
                            {service.correction_requested === "yes" &&
                              service.correction_resolved === "no" && (
                                <span className={styles.pendingBadge}>
                                  Pending
                                </span>
                              )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                >
                  Previous
                </button>
                <span className={styles.paginationInfo}>
                  Page {currentPage} of {totalPages} ({filteredServices.length}{" "}
                  services)
                </span>
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className={styles.paginationButton}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>
            <div>No services found</div>
          </div>
        )}
      </div>

      {/* Correction Request Modal */}
      {showCorrectionModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Request Correction</h2>
              <button
                onClick={() => {
                  setShowCorrectionModal(false);
                  setCorrectionReason("");
                  setSelectedService(null);
                }}
                className={styles.closeBtn}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>
                Service ID: <strong>{selectedService?.service_id}</strong>
              </p>
              <p>
                Customer: <strong>{selectedService?.customer_name}</strong>
              </p>
              <p>
                Date:{" "}
                <strong>{formatDate(selectedService?.service_date)}</strong>
              </p>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Reason for Correction *
                </label>
                <textarea
                  value={correctionReason}
                  onChange={(e) => setCorrectionReason(e.target.value)}
                  className={styles.formTextarea}
                  rows="4"
                  placeholder="Please explain what needs to be corrected..."
                  required
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={() => {
                  setShowCorrectionModal(false);
                  setCorrectionReason("");
                  setSelectedService(null);
                }}
                className={`${styles.modalBtn} ${styles.cancelBtn}`}
              >
                Cancel
              </button>
              <button
                onClick={submitCorrectionRequest}
                className={`${styles.modalBtn} ${styles.submitBtn}`}
                disabled={!correctionReason.trim()}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Delete Service</h2>
              <button onClick={cancelDeleteService} className={styles.closeBtn}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.deleteWarning}>
                ⚠️ Are you sure you want to delete this service record?
              </p>
              <div className={styles.deleteDetails}>
                <p>
                  Service ID: <strong>{serviceToDelete?.service_id}</strong>
                </p>
                <p>
                  Customer: <strong>{serviceToDelete?.customer_name}</strong>
                </p>
                <p>
                  Date:{" "}
                  <strong>{formatDate(serviceToDelete?.service_date)}</strong>
                </p>
                {serviceToDelete?.receipt_no && (
                  <p>
                    Receipt #: <strong>{serviceToDelete.receipt_no}</strong>
                  </p>
                )}
              </div>
              <p className={styles.deleteWarningText}>
                This action cannot be undone. The service record will be
                permanently removed from the database.
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={cancelDeleteService}
                className={`${styles.modalBtn} ${styles.cancelBtn}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteService}
                className={`${styles.modalBtn} ${styles.deleteConfirmBtn}`}
              >
                Delete Service
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StringerDashboard;
